import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { isEmptyString } from '@helpers/utils';

export type RouteUrlHistory = {
  previousRoute: string | null;
  searchQueryParams: string | null;
};
export const RouteUrlHistoryDefaultValue = {
  previousRoute: null,
  searchQueryParams: null,
};

export const RouteUrlHistoryContext = createContext<RouteUrlHistory>(
  RouteUrlHistoryDefaultValue,
);

export const RouteUrlHistoryProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const router = useRouter();
  const [previousRoute, setPreviousRoute] = useState<string | null>(null);
  const [searchQueryParams, setSearchQueryParams] = useState<string | null>(
    null,
  );

  /**
   * Saves the current URL & Search param before changing the route.
   */
  const handleBeforeHistoryChange = (url: string) => {
    const [nextUrl] = url?.split('?') || [];
    const nextUrlWithoutCatalogPath =
      nextUrl.split('catalog')[1] || nextUrl.split('catalog')[0];
    /**
     * This ensures that future URI/URL searches after this point do not show previous free text search in breadcrumb
     */
    if (nextUrl === '/catalog') {
      setSearchQueryParams(null);
    }

    if (typeof router.query.q === 'string' && !isEmptyString(router.query.q)) {
      const [_, queryString] = router.asPath.split('?');
      setSearchQueryParams(queryString);
    }

    if (nextUrlWithoutCatalogPath !== router.asPath.split('?')[0]) {
      setPreviousRoute(router.asPath === '/' ? '/catalog' : router.asPath);
    }
  };

  useEffect(() => {
    router.events.on('beforeHistoryChange', handleBeforeHistoryChange);

    return () => {
      router.events.off('beforeHistoryChange', handleBeforeHistoryChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleBeforeHistoryChange]);

  return (
    <RouteUrlHistoryContext.Provider
      value={{ previousRoute, searchQueryParams }}
    >
      {children}
    </RouteUrlHistoryContext.Provider>
  );
};

export const useRouteUrlHistory = () => {
  return useContext(RouteUrlHistoryContext);
};

export default RouteUrlHistoryProvider;
